import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import makeStyles from '@mui/styles/makeStyles';
import { FormatNumberOptions, FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  SingleProductPromotionInfo,
  getFeatureFromPlan,
  getIntervalPathParam,
  getQuotaPathParam,
  getUnitName,
  Feature, ProductGroup, PreDefinedProduct, Package,
} from '../../../../models';
import { fromPromotion } from '../../../../store';
import { checkout } from '../../../../layout/routes';
import { ReactComponent as MostPopular } from '../../../../components/icons/mostPopular.svg';
import { TrialProducts } from './TrialProducts';
import getFeatureGroup = Feature.getFeatureGroup;
import { sendTrackingData } from '../../../../utils/utils';
import { USD_CURRENCY } from '../../../../utils/Constants';
import { useGetTrackingCookies } from '../../../../hook/useGetTrackingCookies';

const useStyles = makeStyles(theme => ({
  rootContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    borderRadius: theme.spacing(0.5),
    background: 'white',
    overflowX: 'scroll',
  },
  packageGroupBox: {
    minWidth: 296,
    position: 'relative',
    borderRadius: theme.spacing(0.5),
    border: 'solid 1px #dbdde5',
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(7),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1,
    backgroundColor: 'white',
  },
  selectedGroupBox: {
    border: 'solid 2px #1847ca',
  },
  groupName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#233145',
    marginBottom: theme.spacing(1),
  },
  selectedGroupName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1847ca',
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2),
  },
  price: {
    fontSize: 20,
    fontWeight: 600,
    color: '#233145',
    margin: 0,
  },
  selectedPrice: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1847ca',
    margin: 0,
  },
  originalPrice: {
    fontSize: 14,
    fontWeight: 500,
    color: '#808b9a',
    textDecoration: 'line-through',
    marginLeft: theme.spacing(1.5),
  },
  packageOptions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  packageRadioGroup: {
    gap: 0,
  },
  packageRadioLabel: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
      fontWeight: 600,
      color: '#233145',
    },
  },
  productsList: {
    marginTop: theme.spacing(2),
    marginBottom: 0,
  },
  productSection: {
    marginBottom: theme.spacing(1.5),
  },
  productSectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#727993',
    marginBottom: theme.spacing(0.5),
  },
  productItem: {
    fontSize: 14,
    fontWeight: 500,
    color: '#233145',
    marginLeft: 0,
  },
  productRadioGroup: {
    marginTop: theme.spacing(0.5),
  },
  productRadioLabel: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
      fontWeight: 500,
      color: '#233145',
    },
  },
  selectButton: {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
    width: 'auto',
    borderRadius: theme.spacing(0.5),
    fontSize: 14,
    fontWeight: 500,
    height: 32,
    textTransform: 'none',
    '&:hover': {
      background: '#002DAD',
    },
  },
  ribbon: {
    width: 'fit-content',
    position: 'absolute',
    right: 0,
    top: 24,
    margin: 0,
  },
  trialTestOpsText: {
    fontSize: 14,
    fontWeight: 500,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& .MuiAlert-message': {
      padding: 0,
    },
  },
}));

interface PromotionPackagesProps {
  accountId: number;
  campaignCode?: string;
}

const formatOptions: FormatNumberOptions = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
};

export const PromotionPackages: React.FC<PromotionPackagesProps> = ({
  accountId,
  campaignCode,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const navigate = useNavigate();
  const promotionInfo = useSelector(fromPromotion.selectActivePromotion);
  const loading = useSelector(fromPromotion.selectPromotionLoading);

  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState<number>(0);
  const [selectedProductOptions, setSelectedProductOptions] = useState<{
    [key: string]: number;
  }>({});
  const trackingCookies = useGetTrackingCookies();
  // Get package groups from SingleProductPromotionInfo
  const packageGroups = (promotionInfo as SingleProductPromotionInfo)?.packageGroups || [];

  const handleGroupClick = (groupIndex: number) => {
    setSelectedGroupIndex(groupIndex);
    setSelectedPackageIndex(0); // Reset to first package in group
    setSelectedProductOptions({}); // Reset product selections
  };

  const handleProductOptionChange = (
    groupIndex: number,
    category: string,
    productIndex: number,
  ) => {
    // Select this group when any radio is clicked
    setSelectedGroupIndex(groupIndex);
    // Reset all product selections and set only the current one
    setSelectedProductOptions({
      [category]: productIndex,
    });
  };

  const handleSelectClick = () => {
    if (selectedGroupIndex === null) return;

    const selectedGroup = packageGroups[selectedGroupIndex];
    const currentPackage = selectedGroup.packages[selectedPackageIndex]
      || selectedGroup.packages[0];

    // Get all products from the selected package
    const allProductsByCategory = getAllProductsByCategory(selectedGroup);
    const selectedProducts: PreDefinedProduct[] = [];

    // Collect selected products based on user's radio button choices
    Object.entries(allProductsByCategory).forEach(([category, products]) => {
      const selectedProductIndex = selectedProductOptions[category] || 0;
      if (products && products[selectedProductIndex]) {
        selectedProducts.push(products[selectedProductIndex]);
      }
    });

    // If no products were explicitly selected (single product case), use all from current package
    const productsToCheckout = selectedProducts.length > 0
      ? selectedProducts
      : currentPackage.discountedProducts;

    // Build query params similar to PriceSummary.tsx
    const params: { [key: string]: string } = {
      accountId: accountId.toString(),
    };

    // Add product details to params
    productsToCheckout.forEach(product => {
      const feature = getFeatureFromPlan(product.productCode);
      if (feature) {
        const quotaParam = getQuotaPathParam(feature);
        const cycleParam = getIntervalPathParam(feature);

        if (quotaParam) {
          params[quotaParam] = product.quantity.toString();
        }

        // Extract billing interval from product code
        params[cycleParam] = 'YEAR';
      }
    });

    sendTrackingData(
      'first_3_clicked',
      accountId,
      undefined,
      {
        page: 'subscription_management',
        currency: USD_CURRENCY,
        value: currentPackage.discountedPrice,
        ...trackingCookies,
        items: [
          ...productsToCheckout.map(product => ({
            item_id: product.productCode,
            item_name: getFeatureFromPlan(product.productCode),
            item_type: 'product',
            quantity: product.quantity,
            price: product.originPrice ?? 0,
            discount: (product.originPrice ?? 0) - (product.discountedPrice ?? 0),
          })),
          ...selectedGroup.trialProducts.map(product => ({
            item_id: product.productCode,
            item_name: getFeatureFromPlan(product.productCode),
            item_type: 'trial_product',
            quantity: product.quantity,
            price: product.originPrice ?? 0,
            discount: (product.originPrice ?? 0) - (product.discountedPrice ?? 0),
          })),
        ],
      },
    );

    // Navigate to checkout
    const queryString = new URLSearchParams(params).toString();
    navigate(`${checkout.path}?${queryString}`);
  };

  const getFeatureTranslationKey = (productCode: string): string => {
    const feature = getFeatureFromPlan(productCode);
    if (feature) {
      return `feature.${feature.toLowerCase()}.product.name`;
    }
    return productCode;
  };

  // Group products by productCode to identify sections (Test Creation, Test Execution, etc.)
  const getProductsByCategory = (products:PreDefinedProduct[]) => {
    const categories: { [key: string]: { productCode: string; quantity: number }[] } = {};

    products.forEach(product => {
      const feature = getFeatureFromPlan(product.productCode);
      const category = getFeatureGroup(feature);

      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(product);
    });

    return categories;
  };

  // Get all unique products from all packages in a group, organized by category
  const getAllProductsByCategory = (group: any) => {
    let allProducts: PreDefinedProduct[] = [];

    group.packages.forEach((pkg: Package) => {
      allProducts = [
        ...allProducts,
        ...pkg.discountedProducts,
      ];
    });

    // Remove duplicates based on productCode and quantity
    const uniqueProducts = allProducts.filter(
      (product, index, self) => (
        index === self.findIndex(
          p => p.productCode === product.productCode && p.quantity === product.quantity,
        )
      ),
    );

    return getProductsByCategory(uniqueProducts);
  };

  // Check if category has multiple product options
  const hasSameProductInCategory = (
    _category: string,
    allCategoryProducts: { productCode: string; quantity: number }[],
  ) => {
    if (allCategoryProducts.length <= 1) return true;

    // Check if all products in category have same productCode (different quantities only)
    const firstProductCode = allCategoryProducts[0].productCode.split('_')[0];
    return allCategoryProducts.every(p => p.productCode.startsWith(firstProductCode));
  };

  // Find matching package based on selected products
  const findMatchingPackage = (
    group: any,
    selectedOptions: { [key: string]: number },
  ) => {
    const allProductsByCategory = getAllProductsByCategory(group);
    const selectedProducts: { productCode: string; quantity: number }[] = [];

    // Collect selected products based on user's radio button choices
    Object.entries(allProductsByCategory).forEach(([category, products]) => {
      const selectedProductIndex = selectedOptions[category] || 0;
      if (products && products[selectedProductIndex]) {
        selectedProducts.push(products[selectedProductIndex]);
      }
    });

    // Find package that matches the selected products
    if (selectedProducts.length > 0) {
      const matchingPackage = group.packages.find((pkg: any) => {
        if (pkg.discountedProducts.length !== selectedProducts.length) return false;

        return selectedProducts.every(
          (selectedProd: any) => pkg.discountedProducts.some(
            (pkgProd: any) => pkgProd.productCode === selectedProd.productCode
              && pkgProd.quantity === selectedProd.quantity,
          ),
        );
      });

      return matchingPackage || group.packages[0];
    }

    return group.packages[0];
  };

  // Don't render if loading, no campaign, or no package groups
  if (loading || !campaignCode || packageGroups.length === 0) {
    return null;
  }

  return (
    <Box className={classes.rootContainer}>
      {packageGroups.map((group, groupIndex) => {
        const isSelected = selectedGroupIndex === groupIndex;
        const allProductsByCategory = getAllProductsByCategory(group);

        // Find matching package based on selected product options
        const currentPackage = isSelected
          ? findMatchingPackage(group, selectedProductOptions)
          : group.packages[0];

        return (
          <Paper
            key={`${group.groupName}`}
            className={`${classes.packageGroupBox} ${isSelected ? classes.selectedGroupBox : ''}`}
            onClick={() => handleGroupClick(groupIndex)}
            elevation={0}
          >
            {group.mostPopular && (
              <MostPopular className={classes.ribbon} />
            )}

            <Typography className={isSelected ? classes.selectedGroupName : classes.groupName}>
              {group.groupName}
            </Typography>

            <Box className={classes.priceContainer}>
              <Typography className={isSelected ? classes.selectedPrice : classes.price}>
                {intl.formatNumber(currentPackage.discountedPrice, formatOptions)}
              </Typography>
              {currentPackage.originPrice > currentPackage.discountedPrice && (
                <Typography className={classes.originalPrice}>
                  {intl.formatNumber(currentPackage.originPrice, formatOptions)}
                </Typography>
              )}
            </Box>

            {/* Product List */}
            <Box className={classes.productsList}>
              {Object.entries(allProductsByCategory).map(([category, products]) => {
                const showAsRadio = !hasSameProductInCategory(category, products);
                const requiresAsterisk = category === ProductGroup.TEST_CREATION
                  || (category === ProductGroup.TEST_EXECUTION && products.length > 1);

                return (
                  <Box key={category} className={classes.productSection}>
                    <Typography className={classes.productSectionTitle}>
                      <FormattedMessage id={`product.group.${category}.title`} />
                      {requiresAsterisk && ' *'}
                    </Typography>
                    {!showAsRadio ? (
                      // Single product or same product - radio for quantity
                      products.length === 1 ? (
                        <Typography className={classes.productItem}>
                          <FormattedMessage
                            id="product.item"
                            values={{
                              productName: intl.formatMessage({
                                id: getFeatureTranslationKey(products[0].productCode),
                              }),
                              quantity: products[0].quantity,
                              unit: intl.formatMessage({
                                id: getUnitName(
                                  getFeatureFromPlan(products[0].productCode),
                                  products[0].quantity,
                                ),
                              }),
                            }}
                          />
                        </Typography>
                      ) : (
                        <RadioGroup
                          className={classes.productRadioGroup}
                          value={
                            isSelected
                              ? (selectedProductOptions[category] || 0).toString()
                              : ''
                          }
                          onChange={e => {
                            handleProductOptionChange(
                              groupIndex,
                              category,
                              parseInt(e.target.value, 10),
                            );
                          }}
                        >
                          {products.map((product, idx) => (
                            <FormControlLabel
                              key={`${product.productCode}-${product.quantity}`}
                              value={idx.toString()}
                              control={<Radio size="small" />}
                              label={`${intl.formatMessage({ id: getFeatureTranslationKey(product.productCode) })} x ${product.quantity} ${intl.formatMessage({ id: getUnitName(getFeatureFromPlan(product.productCode), product.quantity) })}`}
                              className={classes.productRadioLabel}
                              onClick={e => e.stopPropagation()}
                            />
                          ))}
                        </RadioGroup>
                      )
                    ) : (
                      // Different products - show as radio for product selection
                      <RadioGroup
                        className={classes.productRadioGroup}
                        value={
                          isSelected
                            ? (selectedProductOptions[category] || 0).toString()
                            : ''
                        }
                        onChange={e => {
                          handleProductOptionChange(
                            groupIndex,
                            category,
                            parseInt(e.target.value, 10),
                          );
                        }}
                      >
                        {products.map((product, idx) => (
                          <FormControlLabel
                            key={`${product.productCode}-${product.quantity}`}
                            value={idx.toString()}
                            control={<Radio size="small" />}
                            label={intl.formatMessage(
                              { id: 'product.item' },
                              {
                                productName: intl.formatMessage({
                                  id: getFeatureTranslationKey(product.productCode),
                                }),
                                quantity: product.quantity,
                                unit: intl.formatMessage({
                                  id: getUnitName(
                                    getFeatureFromPlan(product.productCode),
                                    product.quantity,
                                  ),
                                }),
                              },
                            )}
                            className={classes.productRadioLabel}
                            onClick={e => e.stopPropagation()}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  </Box>
                );
              })}
            </Box>

            {group.trialProducts.length > 0 && (
              <TrialProducts trialProducts={getProductsByCategory(group.trialProducts)} />
            )}

            <Button
              variant="contained"
              color="primary"
              className={classes.selectButton}
              onClick={e => {
                e.stopPropagation();
                handleSelectClick();
              }}
              disabled={!isSelected}
            >
              <FormattedMessage id="promotion.select.button" defaultMessage="Select" />
            </Button>
          </Paper>
        );
      })}
    </Box>
  );
};
